'use client';
import React from 'react';
import { FaLock } from 'react-icons/fa6';
import { useBenzingaEdge } from '@benzinga/edge';
import { Impression } from '@benzinga/analytics';
import type { SeeMoreRankingData, SeeMoreRankingTicker } from '@benzinga/blocks-utils';

interface SeeMoreRankingProps {
  attrs?: {
    data?: SeeMoreRankingData;
  };
}

const themeClasses = (theme: 'light' | 'dark') =>
  theme === 'dark'
    ? {
        border: 'border-[#283d59]',
        link: 'text-bzblue-300 hover:text-bzblue-200',
        logoKey: 'mark_light',
        muted: 'text-[#B8CBE5]',
        score: 'text-white',
        surface: 'bg-bzblue-1100 text-white',
        title: 'text-white',
      }
    : {
        border: 'border-[#e1ebfa]',
        link: 'text-bzblue-700 hover:text-bzblue-800',
        logoKey: 'mark_dark',
        muted: 'text-[#5b7292]',
        score: 'text-[#192940]',
        surface: 'bg-bzblue-100 text-[#192940]',
        title: 'text-[#192940]',
      };

const scoreColor = (value?: number) => {
  if (value === undefined || value === null) return 'text-gray-400';
  if (value >= 70) return 'text-green-600';
  if (value >= 40) return 'text-yellow-500';
  return 'text-red-500';
};

const ScoreBadge: React.FC<{
  hasEdge: boolean;
  label: string;
  large?: boolean;
  themeKey: ReturnType<typeof themeClasses>;
  value?: number;
}> = ({ hasEdge, label, large = false, themeKey, value }) => {
  const display = typeof value === 'number' ? value.toFixed(1) : 'N/A';
  return (
    <div className="flex flex-col-reverse items-center justify-center text-center">
      <span
        aria-hidden={!hasEdge}
        className={`font-bold leading-none ${large ? 'text-3xl' : 'text-2xl'} ${
          hasEdge ? scoreColor(value) : `${themeKey.score} blur-md select-none pointer-events-none`
        }`}
        title={hasEdge ? `${label} ranking` : 'Subscribe to Benzinga Edge to view rankings'}
      >
        {hasEdge ? display : '88'}
      </span>
      <span className={`mt-1 flex items-center gap-1 text-[11px] uppercase tracking-wide ${themeKey.muted}`}>
        {!hasEdge && <FaLock size={9} />}
        {label}
      </span>
    </div>
  );
};

const RankingRow: React.FC<{
  hasEdge: boolean;
  large?: boolean;
  rankingLabel: string;
  themeKey: ReturnType<typeof themeClasses>;
  ticker: SeeMoreRankingTicker;
}> = ({ hasEdge, large, rankingLabel, themeKey, ticker }) => {
  return (
    <a
      aria-hidden={!hasEdge}
      aria-label={hasEdge ? `View ${ticker.symbol} ranking` : 'Subscribe to Benzinga Edge to view rankings'}
      className={`flex items-center gap-3 rounded-lg border px-3 py-1 no-underline transition-colors hover:bg-black/[0.03] ${themeKey.border}`}
      href={
        hasEdge
          ? `/quote/${ticker.symbol}`
          : `https://www.benzinga.com/screener/top-${rankingLabel.toLowerCase()}-stocks`
      }
      rel="noopener noreferrer"
      target="_blank"
    >
      <div
        className="flex min-w-0 flex-1 flex-col"
        title={hasEdge ? `View ${ticker.symbol} ranking` : 'Subscribe to Benzinga Edge to view this ranking'}
      >
        <span
          className={
            `truncate text-sm md:text-lg font-bold ${themeKey.title}` +
            (!hasEdge ? ' blur-sm select-none pointer-events-none' : '')
          }
        >
          {hasEdge ? ticker.symbol : 'BZE'}
        </span>
      </div>
      <ScoreBadge
        hasEdge={hasEdge}
        label={rankingLabel}
        large={large}
        themeKey={themeKey}
        value={ticker.rankingValue}
      />
    </a>
  );
};

const SeeMoreRankingBlockContent: React.FC<SeeMoreRankingProps> = ({ attrs }) => {
  const { hasEdgeSubscription } = useBenzingaEdge();
  const data = attrs?.data;

  if (!data) {
    return null;
  }

  const { rankingLabel, rankings, theme, title, url, variant } = data;
  const themeKey = themeClasses(theme);
  const baseClasses =
    'see-more-ranking-block my-4 rounded-md border px-4 py-3 flex flex-row justify-between items-center gap-2 md:gap-4 max-w-[720px] w-full mx-auto';

  const ButtonLink = ({ href, label }: { href: string; label: string }) => (
    <a
      className={`px-2 md:px-4 py-2 rounded-md bg-bzblue-700 text-white font-semibold text-sm md:text-base border-[1.5px] border-white/25 text-center hover:bg-bzblue-800 transition-colors`}
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {label}
    </a>
  );

  if (variant === 'basic') {
    return (
      <div className={`${baseClasses} ${themeKey.surface} ${themeKey.border} text-lg md:text-2xl`}>
        <a
          className={`decoration-none ${themeKey.title} hover:underline`}
          href={url}
          rel="noopener noreferrer"
          target="_blank"
        >
          View the <strong className="font-extrabold">{title}</strong>
        </a>
        <ButtonLink href={url} label="SEE MORE" />
      </div>
    );
  }

  if (variant === 'basic-2') {
    return (
      <div className={`${baseClasses} ${themeKey.surface} ${themeKey.border}`}>
        <div>
          <a
            className={`decoration-none ${themeKey.title} hover:underline`}
            href={url}
            rel="noopener noreferrer"
            target="_blank"
          >
            <span className={`text-lg md:text-2xl font-extrabold ${themeKey.title}`}>{title}</span>
          </a>
          <div className={`text-xs ${themeKey.muted}`}>Ranked by Benzinga Edge&apos;s Exclusive System</div>
        </div>
        <ButtonLink href={url} label="SEE MORE" />
      </div>
    );
  }

  if (variant === 'single-compact') {
    const ticker = rankings?.[0] ?? {
      logo: undefined,
      name: data.ticker,
      rankingValue: undefined,
      symbol: data.ticker,
    };
    return (
      <div className={`${baseClasses} ${themeKey.surface} ${themeKey.border} flex-wrap`}>
        <div
          className={`rounded-full border w-10 h-10 flex items-center justify-center ${themeKey.border} bg-bzblue-200`}
        >
          {ticker.logo && (
            <img alt={ticker.symbol} className="h-full w-full" src={ticker.logo.files[themeKey.logoKey]} />
          )}
        </div>
        <div className="flex flex-1 flex-col">
          <div
            className={`text-xs font-bold tracking-widest uppercase ${themeKey.muted}`}
            style={{ letterSpacing: '0.1em' }}
          >
            {title}
          </div>
          <div className="flex flex-row items-center gap-2">
            <a
              className={`decoration-none ${themeKey.title} hover:underline`}
              href={`/quote/${ticker.symbol}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              <span className={`text-lg md:text-2xl font-extrabold ${themeKey.title}`}>{ticker.symbol}</span>
            </a>
            <div className={`text-sm md:text-base ${themeKey.muted}`}>{ticker.name}</div>
          </div>
        </div>
        <div>
          <ScoreBadge
            hasEdge={hasEdgeSubscription}
            label={rankingLabel}
            large={true}
            themeKey={themeKey}
            value={ticker.rankingValue}
          />
        </div>
        <ButtonLink href={url} label="View Top 100" />
      </div>
    );
  }

  const rows = rankings.slice(0, 2);
  return (
    <div className={`${baseClasses} ${themeKey.surface} ${themeKey.border} flex-wrap`}>
      <div className="flex flex-col items-start">
        <span
          className={`text-xs font-medium tracking-widest uppercase ${themeKey.muted}`}
          style={{ letterSpacing: variant === 'compact-2' ? '0.15em' : '0.25em' }}
        >
          {variant === 'compact-2' ? 'Benzinga Edge' : 'SEE MORE'}
        </span>
        <a
          className={`decoration-none ${themeKey.title} hover:underline`}
          href={url}
          rel="noopener noreferrer"
          target="_blank"
        >
          <span className={`text-xl md:text-2xl font-extrabold ${themeKey.title}`}>{title}</span>
        </a>
      </div>
      {rows.length === 0 ? (
        <div className={`text-base ${themeKey.muted}`}>Rankings are temporarily unavailable.</div>
      ) : (
        <div className="flex flex-1 flex-row gap-2 justify-start md:justify-end">
          {rows.map((ticker, index) => (
            <RankingRow
              hasEdge={hasEdgeSubscription}
              key={ticker.symbol ?? index}
              large={false}
              rankingLabel={rankingLabel}
              themeKey={themeKey}
              ticker={ticker}
            />
          ))}
        </div>
      )}
      <ButtonLink href={url} label="View Top 100" />
    </div>
  );
};

export const SeeMoreRankingBlock: React.FC<SeeMoreRankingProps> = ({ attrs }) => {
  const data = attrs?.data;

  if (!data) {
    return null;
  }

  return (
    <Impression campaign_id="see-more-ranking" unit_type={`${data.rankingType}-${data.variant}`}>
      <SeeMoreRankingBlockContent attrs={attrs} />
    </Impression>
  );
};

export default SeeMoreRankingBlock;
